import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDays, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { printWeek } from './printWeek';
import { Button } from '@/components/ui/button';
import { LessonCard } from './LessonCard';
import { chapterColor } from './planningColors';

export const PlannerLessons = ({ lessons, today, canTick, onChange, focusWeek, isPrimary = false, title = '' }) => {
  const terms = useMemo(() => [...new Set(lessons.map((l) => l.term))], [lessons]);
  const todayLesson = lessons.find((l) => l.lesson_date === today);
  const nextLesson = todayLesson || lessons.find((l) => l.lesson_date && l.lesson_date >= today && l.status !== 'taught') || lessons[0];

  const [term, setTerm] = useState(focusWeek?.term || nextLesson?.term || terms[0]);
  const [week, setWeek] = useState(focusWeek?.week || nextLesson?.week || 1);
  useEffect(() => { if (focusWeek) { setTerm(focusWeek.term); setWeek(focusWeek.week); } }, [focusWeek]);

  const weeksInTerm = useMemo(() => [...new Set(lessons.filter((l) => l.term === term).map((l) => l.week))].sort((a, b) => a - b), [lessons, term]);
  const shown = lessons.filter((l) => l.term === term && l.week === week);
  const jumpToToday = () => { if (nextLesson) { setTerm(nextLesson.term); setWeek(nextLesson.week); } };

  return (
    <div className="space-y-4" data-testid="planner-lessons">
      <div className="flex flex-wrap items-center gap-2 rounded-xl border bg-white p-3">
        <select value={term} onChange={(e) => { setTerm(e.target.value); setWeek(1); }} className="border rounded-md px-2 py-1.5 text-sm" data-testid="lessons-term-select">
          {terms.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <div className="flex flex-wrap gap-1" data-testid="lessons-week-picker">
          {weeksInTerm.map((w) => {
            const ls = lessons.filter((l) => l.term === term && l.week === w);
            const done = ls.length && ls.every((l) => l.status === 'taught');
            const col = chapterColor(ls.find((l) => l.chapter && l.chapter !== 'General')?.chapter);
            return (
              <button key={w} type="button" onClick={() => setWeek(w)} data-testid={`lessons-week-${w}`}
                className={`h-8 min-w-8 px-2 rounded-md text-xs font-medium border transition-colors ${week === w ? 'bg-emerald-600 text-white border-emerald-600' : done ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : `${col.bg} ${col.text} ${col.border} hover:brightness-95`}`}>
                Wk {w}
              </button>
            );
          })}
        </div>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" disabled={!shown.length} data-testid="lessons-print-week"
            onClick={() => { if (!printWeek({ title, term, week, lessons: shown, isPrimary })) toast.error('Please allow pop-ups to print the week'); }}>
            <Printer className="h-4 w-4 mr-1" />Print week
          </Button>
          <Button size="sm" variant="outline" onClick={jumpToToday} data-testid="lessons-jump-today"><CalendarDays className="h-4 w-4 mr-1" />Today's lesson</Button>
        </div>
      </div>
      {shown.length === 0 ? (
        <p className="text-sm text-slate-500 italic py-8 text-center">No lessons planned for this week.</p>
      ) : (
        <div className="space-y-4">
          {shown.map((l) => <LessonCard key={l.id} lesson={l} canTick={canTick} isToday={l.lesson_date === today} onChange={onChange} isPrimary={isPrimary} />)}
        </div>
      )}
    </div>
  );
};
